import { useEffect, useState } from "react";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import clsx from "clsx";
import { PiggyMark, QuillFeather } from "../Logo";
import { SocialRow } from "../Socials";

const links = [
    { to: "/swap",      label: "Swap"      },
    { to: "/lp",        label: "LP"        },
    { to: "/stake",     label: "Stake"     },
    { to: "/referrals", label: "Referrals" },
    { to: "/mint",      label: "Mint"      },
    { to: "/hall",      label: "Hall"      },
    { to: "/radar",     label: "Radar"     },
];

export default function Nav() {
    const [open, setOpen] = useState(false);
    const location = useLocation();

    // Auto-close on route change (covers tapping a link inside the panel)
    useEffect(() => { setOpen(false); }, [location.pathname]);

    // Close on Escape + lock background scroll while open
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [open]);

    return (
        <>
            <header className="sticky top-0 z-40 backdrop-blur-xl border-b border-white/[0.06] bg-piggy-black/60">
                <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                    <NavLink to="/" className="flex items-center gap-2.5 group">
                        <motion.div
                            className="rounded-2xl shadow-glow"
                            whileHover={{ rotate: -8, scale: 1.05 }}
                            transition={{ type: "spring", stiffness: 400 }}
                        >
                            <PiggyMark size={36} className="rounded-2xl" />
                        </motion.div>
                        <span className="display text-xl font-black text-piggy-cream tracking-tight">Safe Piggy</span>
                        <QuillFeather
                            size={18}
                            tone="lime"
                            className="-ml-1.5 -mt-3 transition-transform group-hover:rotate-6"
                        />
                    </NavLink>

                    <nav className="hidden md:flex items-center gap-1">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                className={({ isActive }) =>
                                    clsx(
                                        "px-4 py-2 rounded-xl text-sm font-medium transition-colors",
                                        isActive
                                            ? "text-piggy-cream bg-white/[0.06]"
                                            : "text-piggy-cream/60 hover:text-piggy-cream hover:bg-white/[0.03]",
                                    )
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3">
                        <div className="hidden sm:block">
                            <SocialRow size="sm" />
                        </div>
                        <div className="hidden sm:block w-px h-6 bg-white/10" />
                        <ConnectButton chainStatus="icon" showBalance={false} accountStatus="address" />
                        <button
                            type="button"
                            aria-label={open ? "Close menu" : "Open menu"}
                            aria-expanded={open}
                            onClick={() => setOpen((o) => !o)}
                            className="md:hidden w-10 h-10 rounded-xl grid place-items-center bg-white/[0.04] hover:bg-white/[0.08] text-piggy-cream"
                        >
                            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile menu — backdrop + slide-down panel. md+ never sees this. */}
            <AnimatePresence>
                {open && (
                    <>
                        <motion.div
                            key="backdrop"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.15 }}
                            className="fixed inset-0 z-30 md:hidden bg-piggy-black/70 backdrop-blur-sm"
                            onClick={() => setOpen(false)}
                            aria-hidden
                        />
                        <motion.div
                            key="panel"
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                            className="fixed top-16 inset-x-0 z-40 md:hidden px-4 pb-6"
                            role="dialog"
                            aria-modal
                            aria-label="Site navigation"
                        >
                            <nav className="glass rounded-3xl p-3 ring-1 ring-white/[0.06]">
                                {links.map((l) => (
                                    <NavLink
                                        key={l.to}
                                        to={l.to}
                                        end={l.to === "/"}
                                        className={({ isActive }) =>
                                            clsx(
                                                "block px-5 py-4 rounded-2xl display text-lg font-bold transition-colors",
                                                isActive
                                                    ? "text-piggy-cream bg-white/[0.06]"
                                                    : "text-piggy-cream/70 hover:text-piggy-cream hover:bg-white/[0.03]",
                                            )
                                        }
                                    >
                                        {l.label}
                                    </NavLink>
                                ))}
                                <div className="flex items-center justify-center gap-3 mt-3 pt-4 border-t border-white/[0.05]">
                                    <SocialRow size="sm" />
                                </div>
                            </nav>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
}
